import { useState } from 'react';
import { ApiError } from '@/api/client';
import { readLimosaPdf, uploadLimosa, type LimosaPerson } from '@/api/administratie';
import { compressImage } from '@/utils/imageCompression';
import { LimosaFileFields, LimosaFilePicker, useLimosaFileForm } from './LimosaFileStep';

/** Upload one Limosa: the PDF or photo first, then for whom, valid from / to, optional number. A PDF fills in the fields. */
export function LimosaUploadForm({
  people,
  initialPartnerId,
  taskId,
  onPartnerChange,
  onUploaded,
}: {
  people: LimosaPerson[];
  /** The job card it is opened from: the upload is linked to that job's project. */
  taskId?: number;
  /** Pre-fills "Voor wie"; still changeable to another of the login's people. */
  initialPartnerId?: number;
  onPartnerChange?: (partnerId: number) => void;
  onUploaded: () => void;
}) {
  const [partnerId, setPartnerId] = useState<number>(
    people.some((p) => p.id === initialPartnerId) ? initialPartnerId! : people[0]?.id ?? 0,
  );
  const form = useLimosaFileForm(readLimosaPdf);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<'te_controleren' | 'goedgekeurd' | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setDone(null);
    const { file, geldigVan, geldigTot } = form;
    if (!file) return setError('Kies een PDF of foto van je Limosa.');
    if (!geldigVan || !geldigTot) return setError('Vul geldig van en geldig tot in.');
    if (geldigVan > geldigTot) return setError('Geldig tot ligt voor geldig van.');

    setBusy(true);
    try {
      const prepared = await compressImage(file);
      const { status } = await uploadLimosa({ partnerId, geldigVan, geldigTot, limosaNummer: form.limosaNummer.trim() || undefined, taskId, file: prepared });
      setDone(status === 'goedgekeurd' ? 'goedgekeurd' : 'te_controleren');
      form.reset();
      (e.target as HTMLFormElement).reset();
      onUploaded();
    } catch (err) {
      setError(err instanceof ApiError && err.message ? err.message : 'Limosa opladen mislukt');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
      <h2 className="font-semibold text-td-navy">Nieuwe Limosa opladen</h2>
      <LimosaFilePicker form={form} />
      {people.length > 1 && (
        <label className="block text-sm text-td-navy">
          Voor wie
          <select
            value={partnerId}
            onChange={(e) => {
              const id = Number(e.target.value);
              setPartnerId(id);
              onPartnerChange?.(id);
            }}
            className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          >
            {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
      )}
      <p className="text-xs text-td-muted">
        Staat een medewerker er niet bij? Vraag het kantoor om hem toe te voegen.
      </p>
      <LimosaFileFields form={form} person={people.find((p) => p.id === partnerId)} />
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      {done && (
        <p className="text-sm text-green-700" role="status">
          {done === 'goedgekeurd' ? 'Opgeladen en goedgekeurd.' : 'Opgeladen. De administratie controleert je Limosa.'}
        </p>
      )}
      <button type="submit" disabled={busy || !form.ready} className="w-full min-h-[48px] bg-td-blue text-white font-semibold rounded-lg disabled:opacity-60">
        {busy ? 'Bezig met opladen…' : 'Opladen'}
      </button>
    </form>
  );
}
